import {
  PDFDocument,
  PDFFont,
  PDFPage,
  rgb,
  StandardFonts,
} from "@cantoo/pdf-lib";

import {
  drawImageInBoxes,
  embedImageFromDataUriSafe,
  SignatureBoxCoordinateType,
} from "./applySignaturesToPdf";

export type PcNameFormatType =
  | "full"
  | "first"
  | "last"
  | "middle"
  | "lastFirstInitial";

export type NameBoxCoordinateType = SignatureBoxCoordinateType & {
  fontSize: number;
  format: PcNameFormatType;
};

export type InitialsBoxCoordinateType = SignatureBoxCoordinateType & {
  fontSize: number;
};

export type RepresentativeNameType = {
  firstName: string;
  middleName?: string | null;
  lastName: string;
};

export type PatientNameType = {
  firstName?: string | null;
  lastName?: string | null;
};

/**
 * Produces the downloadable signed consent PDF by stamping, onto the blank form:
 * the patient signature, the Authorized Representative's signature and typed
 * name (in the per-box format), and the patient's initials. Any piece whose
 * coordinates or source data are absent is skipped, so forms without AR/initials
 * boxes — or when no AR is configured — render without those stamps.
 */
export const fillSignedConsentPdf = async ({
  pdfBytes,
  patientSignatureDataUri,
  signatureBoxCoordinates,
  representativeSignatureDataUri,
  pcSignatureBoxCoordinates,
  representativeName,
  pcNameCoordinates,
  patientName,
  patientInitialsCoordinates,
}: {
  pdfBytes: Uint8Array | ArrayBuffer;
  patientSignatureDataUri: string;
  signatureBoxCoordinates?: SignatureBoxCoordinateType[] | null;
  representativeSignatureDataUri?: string | null;
  pcSignatureBoxCoordinates?: SignatureBoxCoordinateType[] | null;
  representativeName?: RepresentativeNameType | null;
  pcNameCoordinates?: NameBoxCoordinateType[] | null;
  patientName?: PatientNameType | null;
  patientInitialsCoordinates?: InitialsBoxCoordinateType[] | null;
}): Promise<Uint8Array> => {
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();

  if (signatureBoxCoordinates?.length) {
    const image = await embedImageFromDataUriSafe(
      pdfDoc,
      patientSignatureDataUri,
    );
    if (!image) {
      throw new Error("Patient signature could not be embedded");
    }
    drawImageInBoxes({ pages, image, coordinates: signatureBoxCoordinates });
  }

  if (representativeSignatureDataUri && pcSignatureBoxCoordinates?.length) {
    const image = await embedImageFromDataUriSafe(
      pdfDoc,
      representativeSignatureDataUri,
    );
    if (image) {
      drawImageInBoxes({
        pages,
        image,
        coordinates: pcSignatureBoxCoordinates,
      });
    } else {
      console.warn("Representative signature skipped: unembeddable image");
    }
  }

  const initials = patientName ? deriveInitials(patientName) : "";
  const hasRepresentativeName = Boolean(
    representativeName && pcNameCoordinates?.length,
  );
  const hasInitials = Boolean(initials && patientInitialsCoordinates?.length);

  if (hasRepresentativeName || hasInitials) {
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    if (representativeName && pcNameCoordinates?.length) {
      for (const box of pcNameCoordinates) {
        const page = pages[box.page - 1];
        const text = formatRepresentativeName(representativeName, box.format);
        if (!page || !text) continue;
        drawTextInBox({ page, font, text, box });
      }
    }

    if (initials && patientInitialsCoordinates?.length) {
      for (const box of patientInitialsCoordinates) {
        const page = pages[box.page - 1];
        if (!page) continue;
        drawTextInBox({ page, font, text: initials, box });
      }
    }
  }

  return pdfDoc.save();
};

const formatRepresentativeName = (
  { firstName, middleName, lastName }: RepresentativeNameType,
  format: PcNameFormatType,
): string => {
  switch (format) {
    case "full":
      return [firstName, middleName, lastName].filter(Boolean).join(" ");
    case "first":
      return firstName;
    case "middle":
      return middleName ?? "";
    case "last":
      return lastName;
    case "lastFirstInitial":
      return `${lastName}, ${firstName.charAt(0)}.`;
  }
};

const deriveInitials = ({ firstName, lastName }: PatientNameType): string => {
  return [firstName, lastName]
    .filter((name): name is string => Boolean(name))
    .map(name => name.charAt(0).toUpperCase())
    .join("");
};

/**
 * Draws text left-aligned and vertically centered within a box, shrinking the
 * font if the text would overflow the box width. Converts the top-left origin
 * box to pdf-lib's bottom-left origin.
 */
const drawTextInBox = ({
  page,
  font,
  text,
  box,
}: {
  page: PDFPage;
  font: PDFFont;
  text: string;
  box: SignatureBoxCoordinateType & { fontSize: number };
}): void => {
  const pageHeight = page.getHeight();

  let fontSize = box.fontSize;
  while (fontSize > 4 && font.widthOfTextAtSize(text, fontSize) > box.width) {
    fontSize -= 0.5;
  }

  const textHeight = font.heightAtSize(fontSize);
  const pdfY = pageHeight - box.y - box.height;
  const offsetY = (box.height - textHeight) / 2;

  page.drawText(text, {
    x: box.x,
    y: pdfY + offsetY,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  });
};
